import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGetDatasetsInfiniteQuery } from 'src/apiClients';
import type { DatasetListItem, PagedQueryArg } from 'src/apiClients/queries';
import { DEFAULT_PAGE_SIZE } from 'src/apiClients/queries';
import { createStateLabel } from 'src/utils/table-formatters';

import { datasetTableI18n } from '../datasets-section/dataset-table-formatters.i18n';
import { ExpandedPanel } from './ExpandedPanel';
import { useStyles } from './WorkspaceDatasets.styles';
import { i18n } from './WorkspacesSection.i18n';

const COLLAPSED_LIMIT = 10;

const STATE_COLORS: Record<string, string> = {
    A: '#4caf50',
    I: '#bdbdbd',
    X: '#ef5350',
};

const GRID_COLUMNS = 'minmax(100px, 2fr) minmax(100px, 2fr) 90px 80px 110px 110px';

interface WorkspaceDatasetsProps {
    workspaceId: string;
    tenantId: string;
}

export const WorkspaceDatasets = ({ workspaceId, tenantId }: WorkspaceDatasetsProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const [expanded, setExpanded] = useState(false);
    const stateLabel = useMemo(() => createStateLabel(intl), [intl]);

    const queryArg = useMemo<PagedQueryArg>(
        () => ({
            limit: DEFAULT_PAGE_SIZE,
            order: [
                ['state', 'ASC'],
                ['name', 'ASC'],
            ],
            where: { workspaceId },
            extraParams: { tenantId },
        }),
        [workspaceId, tenantId],
    );

    const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } =
        useGetDatasetsInfiniteQuery(queryArg);

    const datasets: DatasetListItem[] = useMemo(() => {
        if (!data) return [];
        return data.pages.flatMap((p: { rows: DatasetListItem[] }) => p.rows);
    }, [data]);

    const total = data?.pages[0]?.total ?? 0;
    // Keep fetching remaining pages while expanded
    useEffect(() => {
        if (expanded && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [expanded, hasNextPage, isFetching, fetchNextPage]);

    const visibleDatasets = expanded ? datasets : datasets.slice(0, COLLAPSED_LIMIT);
    const hasHiddenItems = !expanded && datasets.length > COLLAPSED_LIMIT;

    const formatDate = (iso: string) => {
        if (!iso) return '—';
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return '—';
        return intl.formatDate(date, { dateStyle: 'medium' });
    };

    const columnHeaders = useMemo(
        () => [
            intl.formatMessage(i18n.columnName),
            intl.formatMessage(i18n.columnDescription),
            intl.formatMessage(i18n.columnStatus),
            intl.formatMessage(i18n.columnUnits),
            intl.formatMessage(i18n.columnCreated),
            intl.formatMessage(i18n.columnUpdated),
        ],
        [intl],
    );

    const footer =
        hasHiddenItems || expanded || (isFetching && !isLoading) ? (
            <Box className={classes.showMoreWrapper}>
                {(hasHiddenItems || expanded) && (
                    <Button
                        size="small"
                        variant="text"
                        className={classes.showMoreButton}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded
                            ? intl.formatMessage(i18n.showLess)
                            : intl.formatMessage(i18n.showAllDatasets, { count: total })}
                    </Button>
                )}
                {isFetching && !isLoading && (
                    <>
                        <CircularProgress size={12} />
                        <Typography variant="caption" color="text.secondary">
                            {intl.formatMessage(i18n.loadingMore)}
                        </Typography>
                    </>
                )}
            </Box>
        ) : undefined;

    return (
        <ExpandedPanel
            title={intl.formatMessage(i18n.datasetsTitle)}
            columnHeaders={columnHeaders}
            gridColumns={GRID_COLUMNS}
            isLoading={isLoading}
            isError={!!isError}
            isEmpty={datasets.length === 0}
            loadingMessage={intl.formatMessage(i18n.loadingDatasets)}
            errorMessage={intl.formatMessage(datasetTableI18n.loadErrorMessage)}
            emptyMessage={intl.formatMessage(i18n.noDatasets)}
            footer={footer}
        >
            {(panelClasses) =>
                visibleDatasets.map((ds) => {
                    const color = STATE_COLORS[ds.state] ?? STATE_COLORS.I;

                    return (
                        <Box key={ds.id} className={panelClasses.row}>
                            <Box className={classes.nameCell}>
                                <Box
                                    className={classes.statusDot}
                                    style={{ backgroundColor: color }}
                                />
                                <Typography variant="body2" className={classes.nameText}>
                                    {ds.name || ds.id}
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={panelClasses.cellText}
                                title={ds.description}
                            >
                                {ds.description || '—'}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={panelClasses.cellText}
                            >
                                {stateLabel(ds.state)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={panelClasses.cellText}
                            >
                                {ds.lengthUnits || '—'}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={panelClasses.cellText}
                            >
                                {formatDate(ds.createdAt)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className={panelClasses.cellText}
                            >
                                {formatDate(ds.updatedAt)}
                            </Typography>
                        </Box>
                    );
                })
            }
        </ExpandedPanel>
    );
};
